/**
 * FREE constraint — place anywhere on the floor plane. The persisted anchor
 * carries the position (1 point, or 2 for LINE-anchored types); orientation
 * comes from `sketchobject_rotation` (R key / scroll while placing).
 *
 * Wall-alignment assist (Revit-like): when the cursor is on a wall, the
 * object aligns to that wall — snapped to the nearest quarter-turn with its
 * closest side flush against the cursor-side face, clamped inside the wall
 * span — while still being persisted as a free transform (it does
 * NOT stay attached when the wall later moves; use a WALL-constrained type
 * for that). Away from walls, placement is free, with smart-guide alignment
 * snapping against the other sketch objects (see alignSnap.ts).
 */

import type { Coordinate } from '@pointorama/potree-shared';
import { getSketchObjectAnchorProperty, getSketchObjectRotationProperty, getSketchObjectWidthProperty, getSketchObjectDepthProperty } from '@pointorama/potree-shared';
import { projectMouseToFloor, snapToWallEdge, SKETCH_OBJECT_WALL_SNAP, type WallEdgeHit } from '../../personas/tools/implementations/WallSketchSnapEngine';
import { snapToPeerObjects } from './alignSnap';
import type { PointoramaSketchObject } from '../../utils/PointoramaSketchObject';
import type { PointoramaWallSketch } from '../../utils/PointoramaWallSketch';
import type { Viewer } from '../../viewer/viewer';
import type { PixelHitTester } from '../../personas/tools/modules/selection/PixelHitTester';
import type { Pt2 } from '../../utils/FloorMesh3DBuilder';
import { SketchObjectAnchorKind } from '../types';
import type { RegisteredSketchObjectType } from '../registry';
import { buildFrameFromAnchor, SKETCH_OBJECT_Z_LIFT } from '../glyphs/frame';
import { pickTouchedWallEdgeAt, unclampedOffsetAt, wallEdgeGeometry, wallSideAtPoint, wallSideFromCursor, wallSlideInterval } from './WallConstraint';
import { pointAtArcLength } from '../../utils/skeleton/arcGeometry';
import type { ConstraintStrategy, PlacementContext, ResolvedPlacement } from './types';

/** The anchor points for a free placement centered at (x, y). */
function anchorForType(kind: SketchObjectAnchorKind, x: number, y: number, rotation: number, width: number, floorZ: number): Coordinate[] {
  if (kind === SketchObjectAnchorKind.LINE) {
    const dx = (Math.cos(rotation) * width) / 2;
    const dy = (Math.sin(rotation) * width) / 2;
    return [
      { x: x - dx, y: y - dy, z: floorZ },
      { x: x + dx, y: y + dy, z: floorZ },
    ];
  }
  // POINT — and POLYGON falls back to its centroid point until a
  // click-per-vertex polygon placement UX exists.
  return [{ x, y, z: floorZ }];
}

function resolveAt(x: number, y: number, rotation: number, type: RegisteredSketchObjectType, width: number, depth: number, floorZ: number): ResolvedPlacement | null {
  const anchor = anchorForType(type.anchor, x, y, rotation, width, floorZ);
  const frame = buildFrameFromAnchor(type.anchor, anchor, rotation, width, depth, floorZ + SKETCH_OBJECT_Z_LIFT);
  if (!frame) return null;
  return { frame, anchor, rotation, wall: null };
}

/** Quarter-turn count (relative to the wall tangent on `sideSign`'s side)
 *  nearest `rotation` — picks which object side faces the wall. */
function nearestQuarterTurns(tangent: Pt2, sideSign: number, rotation: number): number {
  const base = Math.atan2(tangent.y * sideSign, tangent.x * sideSign);
  let rel = (rotation - base) % (2 * Math.PI);
  if (rel > Math.PI) rel -= 2 * Math.PI;
  if (rel < -Math.PI) rel += 2 * Math.PI;
  return Math.round(rel / (Math.PI / 2));
}

/** Sticky wall-alignment state of the cursor-driven ghost/drag flows (see
 *  {@link wallAlignedPoseAt}) — the transform gizmo owns its own instance. */
const hoverSticky: { edgeId: string | null } = { edgeId: null };

/**
 * The edge id the FREE ghost is currently wall-aligned to, or null when
 * alignment is disengaged. Read by the sketch-object tool to decide whether
 * a placement actually USED an imagined (transient) wall edge — the cursor
 * sits inside the room during footprint-contact alignment, so a pixel snap
 * on the wall centerline can't answer that.
 */
export function getStickyAlignedEdgeId(): string | null {
  return hoverSticky.edgeId;
}

/** Touch extents for a free object: the half-extents toward/along a
 *  candidate wall follow the quarter-turn the object would snap at. */
function shapeTouchExtents(width: number, depth: number, rotation: number): (tangent: Pt2, side: boolean) => { flushHalf: number; alongHalf: number } {
  return (tangent, side) => {
    const quarterTurns = nearestQuarterTurns(tangent, side ? 1 : -1, rotation);
    return quarterTurns % 2 === 0 ? { flushHalf: depth / 2, alongHalf: width / 2 } : { flushHalf: width / 2, alongHalf: depth / 2 };
  };
}

/** Cursor-driven wrapper of {@link wallAlignedPoseAt} — projects the mouse to
 *  the floor and adds the generous pixel-snap fallback for small objects
 *  hovered near a wall before their footprint reaches it. */
function resolveWallAlignedPose(ctx: PlacementContext, width: number, depth: number, currentRotation: number): { x: number; y: number; rotation: number } | null {
  if (!ctx.wallSketch) return null;
  const floorZ = ctx.wallSketch.getFloorHeight();
  const floor = projectMouseToFloor(ctx.event, ctx.viewer.renderer.domElement, ctx.viewer.scene.getActiveCamera(), floorZ);
  if (!floor) {
    hoverSticky.edgeId = null;
    return null;
  }
  const pixelFallback = snapToWallEdge(ctx.event, ctx.wallSketch, ctx.hitTester, ctx.viewer.scene.getActiveCamera(), ctx.viewer.renderer.domElement, SKETCH_OBJECT_WALL_SNAP);
  return wallAlignedPoseAt(ctx.wallSketch, floor, width, depth, currentRotation, hoverSticky, pixelFallback);
}

/**
 * When the object is at a wall, the aligned pose that puts its CLOSEST side
 * flush against the wall face on `at`'s side. Position-based — `at` plays
 * the cursor's role (the object center for gizmo-driven transforms):
 *  - The wall is chosen by footprint contact ({@link pickTouchedWallEdgeAt}
 *    with quarter-turn-aware extents) — sliding along a wall never jumps
 *    onto a transverse stem whose band happens to contain the point at a
 *    T-junction. `pixelFallback` (cursor flows only) lets small objects
 *    engage before their footprint reaches the wall.
 *  - The object snaps to the quarter-turn (relative to the wall) nearest its
 *    current rotation, so whichever side is already closest to the wall
 *    becomes the flush side — a bath approached side-on attaches by its long
 *    side, rotated end-on (`R`) by its end.
 *  - The along-wall position is clamped GEOMETRICALLY via
 *    {@link wallSlideInterval}: the object sits wherever wall material backs
 *    its flush side (spanning T-junctions, centerline jogs and thickness
 *    steps) and stops against wall bodies protruding into it (real corners)
 *    — it always lands inside the room.
 * Null away from walls (free placement applies). `sticky` keeps the aligned
 * edge stable across calls; each independent flow owns one instance.
 */
export function wallAlignedPoseAt(
  wallSketch: PointoramaWallSketch,
  at: Pt2,
  width: number,
  depth: number,
  currentRotation: number,
  sticky: { edgeId: string | null },
  pixelFallback: WallEdgeHit | null = null,
): { x: number; y: number; rotation: number } | null {
  const hit = pickTouchedWallEdgeAt(wallSketch, at, shapeTouchExtents(width, depth, currentRotation), sticky.edgeId) ?? pixelFallback;
  if (!hit) {
    sticky.edgeId = null;
    return null;
  }
  const geom = wallEdgeGeometry(wallSketch, hit.edgeId);
  if (!geom) {
    sticky.edgeId = null;
    return null;
  }
  sticky.edgeId = hit.edgeId;

  const side = wallSideAtPoint(geom, at);
  // Flip the local X axis on the far side so the frame's +Y (built as +90°-CCW
  // of the rotation in buildFrameFromAnchor) always points AWAY from the wall.
  const sideSign = side ? 1 : -1;

  // Quarter-turn nearest the ghost's current rotation, judged against the
  // LOCAL tangent at the cursor (arc length on curved walls). Even turns put
  // the depth axis across the wall (back/front flush), odd turns the width
  // axis (an end flush).
  const HALF_PI = Math.PI / 2;
  const hitTangent = pointAtArcLength(geom.a, geom.b, geom.bulge, hit.offset).tangent;
  const quarterTurns = nearestQuarterTurns(hitTangent, sideSign, currentRotation);
  const flushHalf = quarterTurns % 2 === 0 ? depth / 2 : width / 2;
  const alongHalf = quarterTurns % 2 === 0 ? width / 2 : depth / 2;

  // Clamp the center's along-wall offset GEOMETRICALLY: the object may sit
  // wherever wall material backs its flush side (support spans T-junctions,
  // centerline jogs and thickness steps) and stops against any wall body
  // protruding into it (real corners). The raw along-line coordinate is used
  // — the pickers clamp their hits to the edge's own span, but the slide
  // interval may extend well beyond it.
  const cursorS = unclampedOffsetAt(geom, at) ?? hit.offset;
  const slide = wallSlideInterval(wallSketch, geom, side, cursorS, flushHalf, alongHalf);
  const lo = slide ? slide.lo : alongHalf;
  const hi = slide ? slide.hi : geom.len - alongHalf;
  const offset = lo <= hi ? Math.min(hi, Math.max(lo, cursorS)) : (lo + hi) / 2;

  const { point, tangent } = pointAtArcLength(geom.a, geom.b, geom.bulge, offset);
  // Measured view aligns flush against the MEASURED face LINE — position AND
  // rotation follow the face (it need not be parallel to the sketch edge), so
  // the ghost matches the persisted pose exactly.
  const faceLine = wallSketch.effectiveMeasuredFaceLineAt(geom.edgeId, side, offset, alongHalf);
  if (faceLine) {
    const rotation = Math.atan2(faceLine.dir.y * sideSign, faceLine.dir.x * sideSign) + quarterTurns * HALF_PI;
    const normX = -faceLine.dir.y * sideSign;
    const normY = faceLine.dir.x * sideSign;
    const t = (point.x - faceLine.point.x) * faceLine.dir.x + (point.y - faceLine.point.y) * faceLine.dir.y;
    const foot = { x: faceLine.point.x + faceLine.dir.x * t, y: faceLine.point.y + faceLine.dir.y * t };
    return { x: foot.x + normX * flushHalf, y: foot.y + normY * flushHalf, rotation };
  }
  const rotation = Math.atan2(tangent.y * sideSign, tangent.x * sideSign) + quarterTurns * HALF_PI;
  const normX = -tangent.y * sideSign;
  const normY = tangent.x * sideSign;
  const centerOff = (side ? geom.leftSize : geom.rightSize) + flushHalf;
  return { x: point.x + normX * centerOff, y: point.y + normY * centerOff, rotation };
}

/** Assist level of {@link resolveFreePlacementAt}: 'full' = wall alignment +
 *  peer smart-guide snap (translations), 'wall' = wall alignment only
 *  (rotation/scale — a peer snap would translate the object as a side
 *  effect), 'none' = raw placement (Shift override). */
export type FreeAlignMode = 'full' | 'wall' | 'none';

/**
 * Position-based FREE placement resolution: the same wall alignment, corner
 * clamping and peer smart-guide snapping the cursor-driven resolveHover /
 * resolveDrag apply, but for a CANDIDATE CENTER computed by something other
 * than the mouse — the transform gizmo's axis-constrained drags. `sticky`
 * is the caller-owned wall-alignment stickiness (one instance per drag
 * flow, so the gizmo never fights the ghost's own state).
 */
export function resolveFreePlacementAt(
  viewer: Viewer,
  hitTester: PixelHitTester,
  wallSketch: PointoramaWallSketch | null,
  floorZ: number,
  selfId: string | null,
  type: RegisteredSketchObjectType,
  point: Pt2,
  rotation: number,
  width: number,
  depth: number,
  sticky: { edgeId: string | null },
  mode: FreeAlignMode,
): ResolvedPlacement | null {
  if (mode === 'none') {
    sticky.edgeId = null;
    return resolveAt(point.x, point.y, rotation, type, width, depth, floorZ);
  }
  if (wallSketch) {
    const aligned = wallAlignedPoseAt(wallSketch, point, width, depth, rotation, sticky);
    if (aligned) return resolveAt(aligned.x, aligned.y, aligned.rotation, type, width, depth, floorZ);
  } else {
    sticky.edgeId = null;
  }
  if (mode === 'wall') return resolveAt(point.x, point.y, rotation, type, width, depth, floorZ);
  const snapped = snapToPeerObjects(viewer, hitTester, selfId, { x: point.x, y: point.y }, rotation, width, depth);
  const placement = resolveAt(snapped.x, snapped.y, rotation, type, width, depth, floorZ);
  if (placement) placement.guides = snapped.guides;
  return placement;
}

/**
 * Placement for a run DRAWN between two snap hits on the same wall edge, for
 * LINE-anchored FREE types (hybrid UX: stampable anywhere, cabinet-style
 * span-draw when hovering a wall). The run aligns to the wall on the
 * cursor-side face, but is persisted as a FREE anchor (two world points) —
 * it does NOT follow later wall edits.
 *
 * The anchor follows the DRAW direction (local +X runs from the first click
 * to the second), so direction-carrying glyphs — the stair's up arrow — point
 * the way the user drew. The run sits flush against the face either way;
 * only which local side faces the wall depends on the draw direction.
 */
export function resolveFreeWallSpan(ctx: PlacementContext, type: RegisteredSketchObjectType, start: WallEdgeHit, end: WallEdgeHit): ResolvedPlacement | null {
  if (!ctx.wallSketch || start.edgeId !== end.edgeId) return null;
  const geom = wallEdgeGeometry(ctx.wallSketch, start.edgeId);
  if (!geom) return null;
  const span = Math.abs(end.offset - start.offset);
  if (span < 1e-4) return null;

  const floorZ = ctx.wallSketch.getFloorHeight();
  const side = wallSideFromCursor(ctx, geom, floorZ, end.point);
  const sideSign = side ? 1 : -1;
  const depth = type.defaultSize.depth;
  const spanMid = (start.offset + end.offset) / 2;
  // Measured view: both ends rest on the MEASURED face LINE (position and
  // implied rotation follow the face, which need not be parallel to the
  // sketch edge).
  const faceLine = ctx.wallSketch.effectiveMeasuredFaceLineAt(geom.edgeId, side, spanMid, span / 2);
  const centerOff = (side ? geom.leftSize : geom.rightSize) + depth / 2;

  // Each anchor point sits off the LOCAL wall face at its own offset — on a
  // curved wall the two ends hug the arc; the run itself stays a straight
  // line between them (it persists as a free 2-point anchor).
  const pointAt = (offset: number): Coordinate => {
    const { point, tangent } = pointAtArcLength(geom.a, geom.b, geom.bulge, offset);
    if (faceLine) {
      const normX = -faceLine.dir.y * sideSign;
      const normY = faceLine.dir.x * sideSign;
      const t = (point.x - faceLine.point.x) * faceLine.dir.x + (point.y - faceLine.point.y) * faceLine.dir.y;
      return {
        x: faceLine.point.x + faceLine.dir.x * t + normX * (depth / 2),
        y: faceLine.point.y + faceLine.dir.y * t + normY * (depth / 2),
        z: floorZ,
      };
    }
    return {
      x: point.x + -tangent.y * sideSign * centerOff,
      y: point.y + tangent.x * sideSign * centerOff,
      z: floorZ,
    };
  };
  const anchor: Coordinate[] = [pointAt(start.offset), pointAt(end.offset)];
  // Rotation follows the DRAW direction (anchor[0] → anchor[1]) so
  // direction-carrying glyphs (the stair's up arrow) point the way drawn.
  const rotation = Math.atan2(anchor[1].y - anchor[0].y, anchor[1].x - anchor[0].x);
  const frame = buildFrameFromAnchor(type.anchor, anchor, rotation, span, depth, floorZ + SKETCH_OBJECT_Z_LIFT);
  if (!frame) return null;
  return { frame, anchor, rotation, wall: null };
}

/**
 * Placement for a run drawn between two arbitrary points (`drawLikeWall`
 * types — beams): a plain LINE anchor from `a` to `b` on the floor plane,
 * the run length as width and the type's default depth. Unlike
 * {@link resolveFreeWallSpan} there is no wall involved — the endpoints
 * themselves may have been snapped onto walls by the caller.
 */
export function resolveFreeRun(type: RegisteredSketchObjectType, a: Pt2, b: Pt2, floorZ: number): ResolvedPlacement | null {
  const width = Math.hypot(b.x - a.x, b.y - a.y);
  if (width < 1e-6) return null;
  const rotation = Math.atan2(b.y - a.y, b.x - a.x);
  const anchor: Coordinate[] = [
    { x: a.x, y: a.y, z: floorZ },
    { x: b.x, y: b.y, z: floorZ },
  ];
  const frame = buildFrameFromAnchor(type.anchor, anchor, rotation, width, type.defaultSize.depth, floorZ + SKETCH_OBJECT_Z_LIFT);
  if (!frame) return null;
  return { frame, anchor, rotation, wall: null };
}

export const freeConstraint: ConstraintStrategy = {
  kind: 'FREE',

  resolveHover(ctx: PlacementContext, type: RegisteredSketchObjectType, rotation: number): ResolvedPlacement | null {
    const { width, depth } = type.defaultSize;
    const aligned = resolveWallAlignedPose(ctx, width, depth, rotation);
    if (aligned) return resolveAt(aligned.x, aligned.y, aligned.rotation, type, width, depth, ctx.floorZ);
    const floor = projectMouseToFloor(ctx.event, ctx.viewer.renderer.domElement, ctx.viewer.scene.getActiveCamera(), ctx.floorZ);
    if (!floor) return null;
    const snapped = snapToPeerObjects(ctx.viewer, ctx.hitTester, null, { x: floor.x, y: floor.y }, rotation, width, depth);
    const placement = resolveAt(snapped.x, snapped.y, rotation, type, width, depth, ctx.floorZ);
    if (placement) placement.guides = snapped.guides;
    return placement;
  },

  resolveDrag(ctx: PlacementContext, object: PointoramaSketchObject, type: RegisteredSketchObjectType): ResolvedPlacement | null {
    const width = getSketchObjectWidthProperty(object.identifier).value ?? type.defaultSize.width;
    const depth = getSketchObjectDepthProperty(object.identifier).value ?? type.defaultSize.depth;
    const rotation = getSketchObjectRotationProperty(object.identifier).value ?? 0;
    const aligned = resolveWallAlignedPose(ctx, width, depth, rotation);
    if (aligned) return resolveAt(aligned.x, aligned.y, aligned.rotation, type, width, depth, ctx.floorZ);
    const floor = projectMouseToFloor(ctx.event, ctx.viewer.renderer.domElement, ctx.viewer.scene.getActiveCamera(), ctx.floorZ);
    if (!floor) return null;
    const snapped = snapToPeerObjects(ctx.viewer, ctx.hitTester, object.identifier, { x: floor.x, y: floor.y }, rotation, width, depth);
    const placement = resolveAt(snapped.x, snapped.y, rotation, type, width, depth, ctx.floorZ);
    if (placement) placement.guides = snapped.guides;
    return placement;
  },

  writePlacement(objectId, values): void {
    if (!values.anchor) return;
    getSketchObjectAnchorProperty(objectId).value = values.anchor;
    getSketchObjectRotationProperty(objectId).value = values.rotation;
  },
};
